import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import {
  HeadlessAuthAlternativeAction,
  HeadlessAuthField,
  HeadlessAuthFlowResult,
  HeadlessAuthStep,
  isKnownStepType,
} from '@app/finxone-web-frontend/app/lib/services/headless-auth-flow/headless-auth-flow.models';
import { HeadlessAuthFlowService } from '@app/finxone-web-frontend/app/lib/services/headless-auth-flow/headless-auth-flow.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { performAutoLoginRedirect } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/onboarding-zone/onboarding-actions.utils';
import {
  BaseCssProperties,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import * as QRCode from 'qrcode';

/**
 * The step types this widget renders. Anything else — including step types the
 * contract knows about but this widget has not been designed for — hits the
 * dead-end panel, because the guide's rule is to fail closed rather than render
 * a half-understood credential prompt.
 *
 * `identify` is the contract's name for the username-only step; `username` is
 * accepted alongside it because some realms label the same step that way.
 */
const SUPPORTED_STEP_TYPES = new Set([
  'username_password',
  'identify',
  'username',
  'password',
  'pin',
  'sms_otp',
  'email_otp',
  'totp',
  'totp_setup',
  'phone_number',
  'update_password',
]);

const STEP_TITLES: Record<string, string> = {
  username_password: 'Sign in',
  identify: 'Identify your account',
  username: 'Identify your account',
  password: 'Enter your password',
  pin: 'Enter your PIN',
  sms_otp: 'Check your phone',
  email_otp: 'Check your email',
  totp: 'Use your authenticator app',
  totp_setup: 'Set up an authenticator app',
  phone_number: 'Add your phone number',
  update_password: 'Choose a new password',
};

const FIELD_LABELS: Record<string, string> = {
  username: 'Username or email address',
  password: 'Password',
  pin: 'PIN',
  phoneNumber: 'Phone number',
  code: 'Verification code',
  otp: 'Authenticator code',
  userLabel: 'Authenticator name',
  selectedCredentialId: 'Authenticator',
  'password-new': 'New password',
  'password-confirm': 'Confirm new password',
};

const ACTION_LABELS: Record<string, string> = {
  forgot_password: 'Forgotten your password?',
  reset_credentials: 'Reset your password by email',
  resend_code: 'Send a new code',
  restart_flow: 'Start again',
  cancel_recovery: 'Cancel and go back',
};

/**
 * Generic copy keyed on the contract's error codes. Deliberately does not widen
 * `invalid_credentials` into "wrong password" or "unknown user" — the guide
 * forbids any wording that enables account enumeration.
 */
const ERROR_COPY: Record<string, string> = {
  invalid_credentials: 'Those sign-in details were not accepted.',
  action_not_available: 'That option is not available for this sign-in.',
  invalid_pin_format: 'That PIN does not match the required format.',
  invalid_phone_number: 'Enter a valid international phone number.',
  invalid_code: 'That code was not accepted.',
  expired_code: 'That code has expired. Request a new one.',
  challenge_unavailable: 'That verification challenge is no longer available.',
  totp_missing: 'Enter the code from your authenticator app.',
  invalid_totp: 'That authenticator code was not accepted.',
  totp_label_missing: 'Give this authenticator a name.',
  totp_label_duplicate: 'That authenticator name is already in use.',
  invalid_identity_attributes: 'Those details did not match our records.',
  password_missing: 'Enter a new password.',
  password_mismatch: 'The passwords do not match.',
  password_rejected: 'That password does not meet the password policy.',
};

const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Fixed black-on-white, never themed: a recoloured QR is a QR a scanner may
 * refuse to read.
 */
const TOTP_QR_OPTIONS: QRCode.QRCodeToDataURLOptions = {
  errorCorrectionLevel: 'M',
  margin: 1,
  width: 176,
  color: { dark: '#000000ff', light: '#ffffffff' },
};

/** Rendered on the app-builder canvas so the widget never calls Keycloak from the builder. */
const BUILDER_PREVIEW_STEP: HeadlessAuthStep = {
  status: 'interaction_required',
  type: 'username_password',
  expiresIn: 300,
  fields: [
    { name: 'username', type: 'text', required: true, autocomplete: 'username' },
    { name: 'password', type: 'password', required: true, autocomplete: 'current-password' },
  ],
  actions: [{ name: 'forgot_password', available: true }],
  metadata: {},
  error: null,
};

/**
 * Renders Keycloak's headless browser flow end to end: whatever step the server
 * returns is turned into a form from its `fields` contract, answers go back to
 * the same authentication session, and the issued tokens are handed to the app's
 * one legitimate auto-login path.
 *
 * Nothing here logs or persists field values, enrollment secrets or tokens.
 */
@Component({
  selector: 'finxone-web-frontend-authentication-flow-widget',
  templateUrl: './authentication-flow-widget.component.html',
  styleUrl: './authentication-flow-widget.component.scss',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
})
export class AuthenticationFlowWidgetComponent implements OnInit, OnDestroy {
  public step: HeadlessAuthStep | null = null;
  public form = new FormGroup<Record<string, FormControl<string | boolean>>>({});
  public busy = false;
  public succeeded = false;
  /** Set when the flow cannot continue: unknown/unsupported step, terminal step, or a thrown error. */
  public deadEndMessage = '';
  public resendCooldown = 0;
  /** Data URL of the enrollment QR; empty when there is nothing to encode or encoding failed. */
  public totpQrDataUrl = '';
  public isBuilderPreview = false;
  public contentStyle: baseCssConfigStyle;

  public readonly dataIn = inject<ProviderClass<UiZoneWidgetAttributeConfig>>(ProviderClass);

  private readonly authFlow = inject(HeadlessAuthFlowService);
  private readonly configService = inject(ConfigService);
  private readonly workflowFormsService = inject(WorkflowFormsService);
  private readonly elementRef = inject(ElementRef);

  private cooldownTimer?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn?.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.COLOR,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BORDER,
      BaseCssProperties.BOX_SHADOW,
    ]);
    this.isBuilderPreview = !!this.configService.getActiveConfigType()?.isWorkflow;

    if (this.isBuilderPreview) {
      this.installStep(BUILDER_PREVIEW_STEP);
      return;
    }
    void this.startFlow();
  }

  ngOnDestroy(): void {
    this.clearCooldown();
    // The step (and with it any enrollment secret, including the QR encoding it)
    // must not outlive the widget.
    this.step = null;
    this.totpQrDataUrl = '';
  }

  public get widgetProperties(): Record<string, unknown> {
    return (this.dataIn?.attri?.widgetProperties ?? {}) as Record<string, unknown>;
  }

  public get title(): string {
    if (this.succeeded) return 'You are signed in';
    return this.step ? (STEP_TITLES[this.step.type] ?? 'Continue signing in') : 'Sign in';
  }

  public get errorMessage(): string {
    const error = this.step?.error;
    if (!error) return '';
    return ERROR_COPY[error.code] ?? error.message ?? 'That request could not be completed.';
  }

  public get maskedDestination(): string {
    const metadata = this.step?.metadata ?? {};
    const destination = metadata['maskedDestination'] ?? metadata['destination'];
    return typeof destination === 'string' ? destination : '';
  }

  public get totpSecret(): string {
    return typeof this.step?.metadata?.['secret'] === 'string' ? (this.step.metadata['secret'] as string) : '';
  }

  public get totpAuthUri(): string {
    return typeof this.step?.metadata?.['otpAuthUri'] === 'string'
      ? (this.step.metadata['otpAuthUri'] as string)
      : '';
  }

  public get authenticationMethods(): Array<{ id: string; label: string }> {
    const methods = this.step?.metadata?.['authenticationMethods'];
    if (!Array.isArray(methods)) return [];
    return methods
      .filter((method): method is Record<string, unknown> => !!method && typeof method === 'object')
      .map((method) => ({
        id: String(method['id'] ?? ''),
        label: String(method['displayName'] ?? method['providerId'] ?? method['id'] ?? 'Another method'),
      }))
      .filter((method) => !!method.id);
  }

  public get alternativeActions(): HeadlessAuthAlternativeAction[] {
    return (this.step?.actions ?? []).filter(
      (action) => action.available && action.name !== 'select_authentication_method',
    );
  }

  public get visibleFields(): HeadlessAuthField[] {
    return (this.step?.fields ?? []).filter((field) => field.type !== 'hidden');
  }

  public actionLabel(action: HeadlessAuthAlternativeAction): string {
    return ACTION_LABELS[action.name] ?? this.humanise(action.name);
  }

  public fieldLabel(field: HeadlessAuthField): string {
    return FIELD_LABELS[field.name] ?? this.humanise(field.name);
  }

  public inputType(field: HeadlessAuthField): string {
    if (['password', 'email', 'tel', 'date'].includes(field.type)) return field.type;
    return 'text';
  }

  /** Code-style entries get a numeric keypad on mobile; everything else stays a plain text field. */
  public inputMode(field: HeadlessAuthField): string | null {
    return ['code', 'otp', 'pin'].some((token) => field.name.toLowerCase().includes(token))
      ? 'numeric'
      : null;
  }

  public isCodeField(field: HeadlessAuthField): boolean {
    return this.inputMode(field) === 'numeric';
  }

  public fieldOptions(field: HeadlessAuthField): Array<{ label: string; value: string }> {
    return (field.options ?? []).map((option) => ({ label: option.label ?? option.value, value: option.value }));
  }

  public isInvalid(field: HeadlessAuthField): boolean {
    const control = this.form.controls[field.name];
    return !!control && control.invalid && control.touched;
  }

  public isActionDisabled(action: HeadlessAuthAlternativeAction): boolean {
    return this.busy || (action.name === 'resend_code' && this.resendCooldown > 0);
  }

  public async onSubmit(): Promise<void> {
    if (this.busy || !this.step) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.focusFirstInvalid();
      return;
    }
    const values: Record<string, string | string[]> = {};
    for (const [name, control] of Object.entries(this.form.controls)) {
      const value = control.value;
      if (value === false || value === '') continue;
      values[name] = value === true ? 'true' : String(value);
    }
    await this.run(() => this.authFlow.submit(values));
  }

  public async onAction(action: HeadlessAuthAlternativeAction): Promise<void> {
    if (this.isActionDisabled(action) || !this.step) return;
    if (action.name === 'resend_code') this.startCooldown();
    await this.run(() => this.authFlow.submit({}, action.name));
  }

  public async onSelectMethod(executionId: string): Promise<void> {
    if (this.busy || !executionId) return;
    await this.run(() => this.authFlow.selectMethod(executionId));
  }

  /** The honest recovery from any dead end: throw the transaction away and start over. */
  public async onRestart(): Promise<void> {
    if (this.busy) return;
    this.authFlow.cancel();
    this.clearCooldown();
    this.deadEndMessage = '';
    this.step = null;
    this.form = new FormGroup<Record<string, FormControl<string | boolean>>>({});
    await this.startFlow();
  }

  public async copyValue(value: string): Promise<void> {
    if (!value) return;
    await navigator.clipboard?.writeText(value);
  }

  private async startFlow(): Promise<void> {
    const loginHint = this.resolveLoginHint();
    await this.run(() => this.authFlow.start(loginHint ? { loginHint } : {}));
  }

  /**
   * Runs one service call, keeping a single request in flight and turning any
   * thrown error into the dead-end panel rather than a blank widget.
   */
  private async run(operation: () => Promise<HeadlessAuthFlowResult>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      this.handleResult(await operation());
    } catch (error) {
      this.failClosed(
        error instanceof Error ? error.message : 'Sign-in could not continue. Please start again.',
      );
    } finally {
      this.busy = false;
    }
  }

  private handleResult(result: HeadlessAuthFlowResult): void {
    if (result.kind === 'tokens') {
      this.completeSignIn(result);
      return;
    }
    const step = result.step;
    if (step.status === 'complete') {
      this.failClosed('That operation finished and cannot sign you in.');
      return;
    }
    if (!SUPPORTED_STEP_TYPES.has(step.type)) {
      this.failClosed(
        isKnownStepType(step.type)
          ? 'This sign-in step is not supported here yet.'
          : 'Sign-in returned a step this application does not recognise.',
      );
      return;
    }
    this.deadEndMessage = '';
    this.installStep(step);
  }

  private completeSignIn(result: Extract<HeadlessAuthFlowResult, { kind: 'tokens' }>): void {
    this.step = null;
    this.succeeded = true;
    this.clearCooldown();
    // performAutoLoginRedirect hard-replaces the document, so nothing may be
    // scheduled after it; the success panel above is whatever the browser paints
    // before the navigation lands.
    performAutoLoginRedirect({
      accessToken: result.tokens.accessToken,
      refreshToken: result.tokens.refreshToken ?? '',
      idToken: result.tokens.idToken,
    });
  }

  private failClosed(message: string): void {
    this.step = null;
    this.totpQrDataUrl = '';
    this.deadEndMessage = message;
    this.clearCooldown();
  }

  private installStep(step: HeadlessAuthStep): void {
    this.step = step;
    const controls: Record<string, FormControl<string | boolean>> = {};
    for (const field of step.fields ?? []) {
      if (field.type === 'hidden') continue;
      const validators: ValidatorFn[] = [];
      if (field.required) validators.push(Validators.required);
      if (field.minLength) validators.push(Validators.minLength(field.minLength));
      if (field.maxLength) validators.push(Validators.maxLength(field.maxLength));
      const initial =
        field.type === 'checkbox' ? false : (field.value ?? field.values?.[0] ?? field.options?.[0]?.value ?? '');
      controls[field.name] = new FormControl<string | boolean>(initial, { nonNullable: true, validators });
    }
    this.form = new FormGroup(controls);
    void this.renderTotpQr();
    queueMicrotask(() => (step.error ? this.focusErrorSummary() : this.focusFirstField()));
  }

  /**
   * Encodes the enrollment URI as a QR image. Failure is not surfaced — the
   * secret and setup-link blocks are the fallback — and neither the URI nor the
   * thrown error is logged, because both carry the enrollment secret.
   */
  private async renderTotpQr(): Promise<void> {
    this.totpQrDataUrl = '';
    const uri = this.totpAuthUri;
    if (this.step?.type !== 'totp_setup' || !uri) return;
    try {
      this.totpQrDataUrl = await QRCode.toDataURL(uri, TOTP_QR_OPTIONS);
    } catch {
      this.totpQrDataUrl = '';
    }
  }

  /**
   * `loginHintFromForm` lets a page collect the identifier before this widget is
   * reached; only the identifier is forwarded, never any credential value.
   */
  private resolveLoginHint(): string {
    if (this.widgetProperties['loginHintFromForm'] !== true) return '';
    for (const formPath of this.workflowFormsService.getCurrentPageForms() ?? []) {
      const form = this.workflowFormsService.getFormPart(formPath);
      const value = form?.get('username')?.value ?? form?.get('email')?.value;
      if (typeof value === 'string' && value) return value;
    }
    return '';
  }

  private startCooldown(): void {
    this.clearCooldown();
    this.resendCooldown = RESEND_COOLDOWN_SECONDS;
    this.cooldownTimer = setInterval(() => {
      this.resendCooldown -= 1;
      if (this.resendCooldown <= 0) this.clearCooldown();
    }, 1000);
  }

  private clearCooldown(): void {
    if (this.cooldownTimer) clearInterval(this.cooldownTimer);
    this.cooldownTimer = undefined;
    this.resendCooldown = 0;
  }

  private focusFirstField(): void {
    this.query('.auth-flow-form input, .auth-flow-form select, .auth-flow-form button')?.focus();
  }

  private focusFirstInvalid(): void {
    this.query('.auth-flow-form [aria-invalid="true"]')?.focus();
  }

  private focusErrorSummary(): void {
    this.query('.auth-flow-error')?.focus();
  }

  private query(selector: string): HTMLElement | null {
    return (this.elementRef.nativeElement as HTMLElement).querySelector<HTMLElement>(selector);
  }

  private humanise(value: string): string {
    const spaced = value.replace(/[-_]/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }
}
